import { useCallback, useEffect, useRef, useState } from 'react';

// One-time-code controller with client-side rate limiting. The server MUST
// enforce the same limits (see api/recovery.js); this just keeps the UI honest
// and gives immediate feedback.
//   - resend cooldown: 30 s
//   - max sends per window: 3, then locked 120 s
//   - max wrong attempts: 5, then locked 60 s
export const OTP_LIMITS = { cooldown: 30, maxSends: 3, sendLock: 120, maxAttempts: 5, attemptLock: 60 };

export const maskEmail = (e) => { const [l, d] = (e || '').split('@'); return l ? `${l.slice(0, 2)}•••@${d || ''}` : ''; };
export const mmss = (n) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
export const isEmail = (s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((s || '').trim());

const initial = () => ({ sent: false, code: '', attempts: 0, sends: 0, cooldown: 0, lockedFor: 0, verified: false, msg: '', target: '', token: null });

/**
 * @param {object} api  { sendCode({target, purpose}) => {challengeId}, verifyCode({challengeId, code}) => {ok, token} }
 * @param {string} purpose 'register' | 'forgot' | 'find' | 'settings'
 */
export function useOtp(api, purpose) {
  const [s, setS] = useState(initial);
  const challenge = useRef(null);

  // 1 Hz tick for cooldown / lock countdowns.
  useEffect(() => {
    if (!s.cooldown && !s.lockedFor) return undefined;
    const id = setInterval(() => setS((x) => {
      const lockedFor = Math.max(0, x.lockedFor - 1), cooldown = Math.max(0, x.cooldown - 1);
      const msg = x.lockedFor > 1 ? `Too many attempts. Try again in ${mmss(lockedFor)}` : x.lockedFor === 1 ? 'You can try again now.' : x.msg;
      return { ...x, lockedFor, cooldown, msg };
    }), 1000);
    return () => clearInterval(id);
  }, [s.cooldown > 0, s.lockedFor > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = useCallback((target = '') => { challenge.current = null; setS({ ...initial(), target }); }, []);

  const send = useCallback(async (target) => {
    const t = (target || s.target || '').trim();
    if (!isEmail(t)) { setS((x) => ({ ...x, msg: 'Enter a valid email address first.' })); return false; }
    if (s.lockedFor > 0 || s.cooldown > 0) return false;
    if (s.sends >= OTP_LIMITS.maxSends) { setS((x) => ({ ...x, lockedFor: OTP_LIMITS.sendLock, msg: `Send limit reached. Try again in ${mmss(OTP_LIMITS.sendLock)}` })); return false; }
    try {
      const r = await api.sendCode({ target: t, purpose });
      challenge.current = r.challengeId;
      setS((x) => ({ ...x, target: t, sent: true, sends: x.sends + 1, cooldown: OTP_LIMITS.cooldown, code: '', attempts: 0, msg: `Code sent to ${maskEmail(t)}. It expires in 10 minutes.` }));
      return true;
    } catch (e) {
      if (e.status === 429) setS((x) => ({ ...x, lockedFor: e.retryAfter || OTP_LIMITS.sendLock, msg: 'Too many requests from this device.' }));
      else setS((x) => ({ ...x, msg: e.message || 'Could not send the code.' }));
      return false;
    }
  }, [api, purpose, s.target, s.lockedFor, s.cooldown, s.sends]);

  const verify = useCallback(async () => {
    if (s.lockedFor > 0 || !s.sent) return false;
    try {
      const r = await api.verifyCode({ challengeId: challenge.current, code: s.code.replace(/\D/g, ''), purpose });
      if (r.ok) { setS((x) => ({ ...x, verified: true, msg: 'Verified.', token: r.token || null })); return r.token || true; }
      throw Object.assign(new Error('bad code'), { status: 400 });
    } catch (e) {
      const attempts = s.attempts + 1;
      if (e.status === 429 || attempts >= OTP_LIMITS.maxAttempts) setS((x) => ({ ...x, attempts: 0, lockedFor: OTP_LIMITS.attemptLock, code: '', msg: `Too many attempts. Try again in ${mmss(OTP_LIMITS.attemptLock)}` }));
      else setS((x) => ({ ...x, attempts, msg: `Incorrect code · ${OTP_LIMITS.maxAttempts - attempts} attempt${OTP_LIMITS.maxAttempts - attempts === 1 ? '' : 's'} left` }));
      return false;
    }
  }, [api, purpose, s.lockedFor, s.sent, s.code, s.attempts]);

  const setCode = useCallback((v) => setS((x) => ({ ...x, code: v.replace(/[^\d ]/g, '').slice(0, 7) })), []);

  const locked = s.lockedFor > 0, cooling = s.cooldown > 0;
  return {
    ...s, send, verify, setCode, reset,
    awaiting: s.sent && !s.verified,
    sendDisabled: locked || cooling || s.verified,
    sendLabel: s.verified ? 'Verified' : locked ? 'Locked' : cooling ? `Resend in ${s.cooldown}s` : s.sent ? 'Resend code' : 'Send code',
    tone: s.verified ? 'ok' : locked || /Incorrect|limit|valid|Too many/.test(s.msg) ? 'bad' : 'muted',
  };
}
